import { useEffect, useState, useCallback } from "react";
import { Loader2, Users, ShieldCheck, AlertTriangle, Target, ClipboardCheck, SlidersHorizontal, Save, X, History as HistoryIcon } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canSeeSecurityDashboard } from "@/lib/roles";

const THRESHOLD_FIELDS = [
  { key: "cohort_target", label: "Cohort target", min: 1, max: 1000 },
  { key: "max_buyer_seller_gap", label: "Max buyer/seller gap", min: 0, max: 1000 },
  { key: "min_categories", label: "Min categories", min: 0, max: 100 },
  { key: "max_unresolved_duplicates", label: "Max unresolved duplicates", min: 0, max: 10000 },
  { key: "min_contact_validation_pct", label: "Min contact validation %", min: 0, max: 100 },
  { key: "min_kyb_ready_pct", label: "Min registry/KYB ready %", min: 0, max: 100 },
  { key: "min_tradematch_verified", label: "Min TradeMatch verified", min: 0, max: 10000 },
  { key: "max_unresolved_red_flags", label: "Max unresolved red flags", min: 0, max: 10000 },
  { key: "min_active_need_coverage_pct", label: "Min active-Need coverage %", min: 0, max: 100 },
];

const Stat = ({ label, value, sub, accent = "text-[#0A0F1C]" }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className={`text-2xl font-bold ${accent}`}>{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
  </div>
);

const Dist = ({ title, data, empty = "No data yet" }) => {
  const entries = Object.entries(data || {});
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-sm font-semibold text-[#0A0F1C] mb-2">{title}</p>
      {entries.length === 0 ? (
        <p className="text-xs text-slate-400">{empty}</p>
      ) : (
        <div className="space-y-1.5">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-sm">
              <span className="capitalize text-slate-600">{String(k).replace(/_/g, " ")}</span>
              <span className="font-semibold text-[#0A0F1C]">{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function CohortReadiness() {
  const { user } = useAuth();
  const isSenior = canSeeSecurityDashboard(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/leads/scout/cohort-readiness")
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
    api.get("/admin/leads/scout/cohort-thresholds/history")
      .then(({ data }) => setHistory(data.history || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEditor = () => { setForm({ ...data.thresholds, reason: "" }); setEditing(true); };
  const saveThresholds = async () => {
    setSaving(true);
    try {
      const payload = { reason: form.reason || "" };
      for (const f of THRESHOLD_FIELDS) payload[f.key] = parseInt(form[f.key], 10);
      await api.put("/admin/leads/scout/cohort-thresholds", payload);
      toast.success("Thresholds updated");
      setEditing(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const c = data?.checklist;

  return (
    <AppShell title="Beta Cohort Readiness" subtitle="Read-only control view for the first 25-organization cohort · no provider calls · no invitations · no live sending.">
      {loading || !data ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm" data-testid="cohort-loading"><Loader2 className="animate-spin" size={16} /> Loading cohort readiness…</div>
      ) : (
        <div className="space-y-6" data-testid="cohort-readiness">
          <div className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 ${
            data.overall_status === "READY" ? "border-green-200 bg-green-50" :
            data.overall_status === "BLOCKED" ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}
            data-testid="cohort-overall-status">
            <div className="flex items-center gap-2">
              {data.overall_status === "READY" ? <ShieldCheck size={18} className="text-green-600" /> : <AlertTriangle size={18} className={data.overall_status === "BLOCKED" ? "text-red-600" : "text-amber-600"} />}
              <span className="font-bold text-[#0A0F1C]">Cohort status: {data.overall_status}</span>
            </div>
            <span className="text-xs text-slate-500">{data.missing?.length || 0} item(s) need attention before approval · internal control only (never auto-invites)</span>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="cohort-gates">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-[#0A0F1C]">Readiness gates</p>
              {isSenior && !editing && (
                <button data-testid="cohort-edit-thresholds" onClick={openEditor}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-[#2563EB] hover:text-[#2563EB]">
                  <SlidersHorizontal size={13} /> Edit thresholds
                </button>
              )}
            </div>
            {editing ? (
              <div data-testid="cohort-threshold-editor">
                <p className="text-xs text-slate-500 mb-3">Senior-only. Changes affect readiness scoring only — they never auto-invite, auto-approve, or bypass verification. Prior values are kept in the audit trail.</p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {THRESHOLD_FIELDS.map((f) => (
                    <label key={f.key} className="text-xs text-slate-600">
                      {f.label} <span className="text-slate-400">({f.min}–{f.max})</span>
                      <input type="number" min={f.min} max={f.max} value={form[f.key] ?? ""}
                        data-testid={`threshold-input-${f.key}`}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        className="mt-1 w-full h-9 px-2 rounded border border-slate-300 text-sm" />
                    </label>
                  ))}
                </div>
                <div className="mt-4 flex gap-2">
                  <button data-testid="threshold-save" onClick={saveThresholds} disabled={saving}
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save thresholds
                  </button>
                  <button data-testid="threshold-cancel" onClick={() => setEditing(false)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600">
                    <X size={13} /> Cancel
                  </button>
                </div>
                <label className="mt-3 block text-xs text-slate-600">Reason / comment (optional)
                  <input value={form.reason || ""} data-testid="threshold-reason"
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    placeholder="Why are you changing these thresholds?"
                    className="mt-1 w-full h-9 px-2 rounded border border-slate-300 text-sm" />
                </label>
              </div>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
              {data.gates.map((g) => (
                <div key={g.label} data-testid={`cohort-gate-${g.label.replace(/[^a-z]/gi, "-").toLowerCase()}`} className="flex items-start justify-between gap-2 border-b border-slate-50 pb-2 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-[#0A0F1C]">{g.label}</p>
                    <p className="text-xs text-slate-500">{g.detail}</p>
                  </div>
                  <span className={`shrink-0 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${
                    g.status === "ready" ? "bg-green-100 text-green-700" :
                    g.status === "blocked" ? "bg-red-100 text-red-700" :
                    g.status === "pending" ? "bg-slate-200 text-slate-600" : "bg-amber-100 text-amber-700"}`}>{g.status}</span>
                </div>
              ))}
            </div>
            )}
          </div>

          {history.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="threshold-change-log">
              <p className="text-sm font-semibold text-[#0A0F1C] mb-2 flex items-center gap-1.5"><HistoryIcon size={14} /> Threshold change history</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map((h, i) => (
                  <div key={i} data-testid={`threshold-log-${i}`} className="border-b border-slate-50 pb-2 last:border-0 text-xs">
                    <div className="text-slate-500">
                      <span className="font-medium text-[#0A0F1C]">{h.changed_by}</span>
                      <span className="ml-1 capitalize">({(h.role || "").replace(/_/g, " ")})</span>
                      <span className="ml-1 text-slate-400">· {String(h.at).slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                      {(h.changes || []).map((ch, j) => (
                        <span key={j} className="text-slate-600">
                          {ch.field.replace(/_/g, " ")}: <span className="text-red-500 line-through">{ch.from}</span> → <span className="text-green-600 font-semibold">{ch.to}</span>
                        </span>
                      ))}
                      {(h.changes || []).length === 0 && <span className="text-slate-400">no value changes</span>}
                    </div>
                    {h.reason && <div className="mt-0.5 text-slate-400">Reason: {h.reason}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Cohort target" value={data.cohort_target} sub="first controlled-beta cohort" accent="text-[#2563EB]" />
            <Stat label="Candidates in pipeline" value={data.candidates_in_pipeline} sub="from the Invitation queue" />
            <Stat label="Slots remaining" value={data.slots_remaining} accent="text-[#C2410C]" />
            <Stat label="Verified & ready" value={data.cohort_verified_ready} sub="passed DEI verification" accent="text-[#16A34A]" />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="cohort-balance">
              <p className="text-sm font-semibold text-[#0A0F1C] mb-2 flex items-center gap-1.5"><Users size={15} /> Buyer / Seller balance</p>
              <div className="flex items-center justify-between text-sm"><span className="text-slate-600">Suppliers</span><span className="font-semibold">{c.buyer_seller_balance.suppliers}</span></div>
              <div className="flex items-center justify-between text-sm"><span className="text-slate-600">Buyers / Investors</span><span className="font-semibold">{c.buyer_seller_balance.buyers_investors}</span></div>
              <span className={`inline-block mt-2 text-[11px] font-semibold px-2 py-0.5 rounded-full ${c.buyer_seller_balance.balanced ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                {c.buyer_seller_balance.balanced ? "Balanced" : "Needs rebalancing"}
              </span>
            </div>
            <Dist title="Industry / category balance" data={c.industry_category_balance} />
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-sm font-semibold text-[#0A0F1C] mb-2 flex items-center gap-1.5"><Target size={15} /> Demand-gap fit</p>
              <div className="flex items-center justify-between text-sm"><span className="text-slate-600">Tied to a genuine gap</span><span className="font-semibold">{c.fills_genuine_demand_gap.tied_to_gap} / {c.fills_genuine_demand_gap.of}</span></div>
              <div className="flex items-center justify-between text-sm mt-1"><span className="text-slate-600">Deduped</span><span className="font-semibold">{c.dedup_status.covered} / {c.dedup_status.of}</span></div>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Dist title="Domain / contact validation (Hunter)" data={c.domain_contact_validation} />
            <Dist title="Registry / KYB status" data={c.registry_kyb_status} empty="Pending — registry/KYB provider held" />
            <Dist title="TradeMatch verification (trust tier)" data={c.tradematch_verification_status.buckets} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Dist title="Invitation review status" data={c.invitation_review_status} />
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="cohort-red-flags">
              <p className="text-sm font-semibold text-[#0A0F1C] mb-2 flex items-center gap-1.5"><AlertTriangle size={15} className="text-amber-500" /> Risk / red flags ({c.risk_red_flags.count})</p>
              {c.risk_red_flags.count === 0 ? (
                <p className="text-xs text-slate-400">No red flags.</p>
              ) : (
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {c.risk_red_flags.items.map((f) => (
                    <div key={f.lead_id} className="text-sm border-b border-slate-50 pb-1.5 last:border-0">
                      <span className="font-medium text-[#0A0F1C]">{f.legal_name}</span>
                      <span className="text-slate-500"> — {f.reasons.join("; ")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500 flex items-start gap-2">
            <ClipboardCheck size={14} className="mt-0.5 shrink-0" /> <span>{data.notice}</span>
          </div>
        </div>
      )}
    </AppShell>
  );
}
